import { useState } from 'react'
import {Copy, MessageCircleDashed as MessageCircle} from 'lucide-react'
import toast from 'react-hot-toast'
import GuideBox from '@/components/GuideBox'
import { copiarTexto } from '@/utils/clipboard'
import {
  obterBeneficioPrincipal,
  obterPrioridadeScript,
} from '@/utils/referenciaOleos'

interface ScriptVendasProps {
  lead: any
}

type PerfilScript = '1' | '2' | '3' | '4'

const perfilLabels: Record<PerfilScript, string> = {
  '1': 'Perfil 1 — Pronta para começar',
  '2': 'Perfil 2 — Interessada mas avaliando',
  '3': 'Perfil 3 — Só quer entender',
  '4': 'Perfil 4 — Obstáculo financeiro',
}

const textoInclui = (valor: string, trechos: string[]): boolean => {
  const normalizado = (valor || '').toLowerCase()
  return trechos.some(t => normalizado.includes(t.toLowerCase()))
}

const identificarPerfilAuto = (lead: any): PerfilScript => {
  const prontidao = lead?.prontidao || ''
  const obstaculo = lead?.obstaculo || ''
  const interesses = Array.isArray(lead?.interesses) ? lead.interesses.join(' ') : String(lead?.interesses || '')

  if (textoInclui(obstaculo, ['Questão financeira', 'financeira'])) return '4'

  const pronta = textoInclui(prontidao, ['Estou pronto', 'Estou pronta', 'quero começar'])
  const semObstaculo = textoInclui(obstaculo, ['Não sinto obstáculo', 'nao sinto obstaculo', 'sem obstáculo'])
    || !obstaculo.trim()
  if (pronta && semObstaculo) return '1'

  const interessada = textoInclui(prontidao, ['Tenho interesse', 'quero saber mais', 'entender melhor'])
  const duvidasUso = textoInclui(obstaculo, ['Tenho dúvidas', 'como usar', 'por onde começar', 'entender'])
  if (interessada || duvidasUso) return '2'

  const soEntender = textoInclui(prontidao, ['Só quero entender', 'nao e prioridade', 'não é prioridade'])
  const bemEstar = textoInclui(interesses, ['bem-estar', 'bem estar'])
  if (soEntender || bemEstar) return '3'

  if (pronta) return '1'
  return '2'
}

const ScriptVendas = ({ lead }: ScriptVendasProps) => {
  const [perfilManual, setPerfilManual] = useState<PerfilScript | null>(null)
  const perfil: PerfilScript = perfilManual || identificarPerfilAuto(lead)

  const primeiroNome = lead?.nome?.trim().split(/\s+/)[0] || 'você'
  const { sintoma: sintomaPrincipal, oleo: oleoPrioritario } = obterPrioridadeScript(lead)
  void obterBeneficioPrincipal(oleoPrioritario)

  const gerarMensagens = (): [string, string, string] => {
    if (perfil === '1') {
      return [
        `Olá ${primeiroNome}! Recebi seu diagnóstico de saúde e emoções que você preencheu.\n\n${sintomaPrincipal}\n\nO óleo essencial mais indicado para o seu momento agora é o ${oleoPrioritario}.\n\nVamos agendar um atendimento para lhe dar orientações de como iniciar o uso, dosagens e protocolos para o seu caso. Isso é importante agora?`,
        'Ótimo. Você prefere agendarmos pela manhã, tarde ou noite?',
        'Perfeito. Nesse atendimento vou confirmar o que você informou no diagnóstico e vamos ver o que é o melhor para você neste momento.',
      ]
    }
    if (perfil === '2') {
      return [
        `Olá ${primeiroNome}! Recebi seu diagnóstico de saúde e emoções que você preencheu.\n\n${sintomaPrincipal}\n\nO óleo essencial mais indicado para o seu momento agora é o ${oleoPrioritario}.\n\nVocê gostaria de conhecer todas as indicações para o seu caso?`,
        'Ótimo! Vamos agendar um atendimento para lhe dar orientações de quais óleos essenciais podem melhorar sua saúde física e emocional. Isso é importante agora?',
        'Você prefere pela manhã, tarde ou noite?',
      ]
    }
    if (perfil === '3') {
      return [
        `Olá ${primeiroNome}! Recebi seu diagnóstico de saúde e emoções que você preencheu.\n\n${sintomaPrincipal}\n\nÉ importante para você hoje priorizar e resolver isso?`,
        `Para o seu perfil o óleo mais indicado para começar seria o ${oleoPrioritario}.\n\nVamos agendar um atendimento para lhe dar orientações de quais óleos essenciais podem melhorar sua saúde física e emocional. Isso é importante agora?`,
        'Ótimo. Você prefere agendarmos manhã, tarde ou noite?',
      ]
    }
    return [
      `Olá ${primeiroNome}! Recebi seu diagnóstico de saúde e emoções que você preencheu.\n\n${sintomaPrincipal}\n\nHoje é importante mudar isso?`,
      `Para o seu perfil o óleo mais indicado para começar seria o ${oleoPrioritario}.\n\nMuitas pessoas acham que os óleos essenciais são caros mas um único frasco dura meses porque se usa apenas 1 gota por vez e um frasco de 5ml tem em média 100 gotas.\n\nVamos agendar um atendimento para lhe dar orientações de quais óleos essenciais podem melhorar sua saúde física e emocional. Isso é importante agora?`,
      'Ótimo. Você prefere agendarmos manhã, tarde ou noite?',
    ]
  }

  const [mensagem1, mensagem2, mensagem3] = gerarMensagens()
  const titulos = [
    'Mensagem 1 — Abertura',
    'Mensagem 2 — Aprofundamento',
    'Mensagem 3 — Agendamento',
  ]

  const copiarMensagem = async (mensagem: string, numero: number) => {
    try {
      await copiarTexto(mensagem)
      toast.success(`Mensagem ${numero} copiada!`)
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  const formatarNumeroWhatsApp = (whatsapp: string): string => {
    const apenasDigitos = (whatsapp || '').replace(/\D/g, '')
    if (!apenasDigitos) return ''
    if (apenasDigitos.startsWith('55') && apenasDigitos.length >= 12) return apenasDigitos
    if (apenasDigitos.startsWith('351')) return apenasDigitos
    if (apenasDigitos.length >= 10 && apenasDigitos.length <= 11) return `55${apenasDigitos}`
    return apenasDigitos
  }

  const abrirWhatsApp = (mensagem: string) => {
    const numero = formatarNumeroWhatsApp(lead?.whatsapp || '')
    if (!numero) {
      toast.error('WhatsApp do cliente não disponível')
      return
    }
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`
    window.open(url, '_blank')
  }

  return (
    <div className="mb-8">
      <GuideBox>
        {`No campo abaixo SCRIPT DE VENDAS siga a sequência com atenção. Envie uma mensagem de cada vez e aguarde sempre a resposta antes de enviar a próxima.

A Mensagem 1 é obrigatória para todos os perfis. Envie assim que o cliente preencher o diagnóstico.

A partir da Mensagem 2 você pode usar exatamente o texto sugerido ou adaptar com uma pergunta baseada no que perceber na resposta do cliente.

A Mensagem 3 tem um único objetivo: marcar o atendimento. Seja direta e objetiva.`}
      </GuideBox>

      <h3 className="text-2xl font-cormorant font-semibold text-verde-escuro mb-4 pb-3 border-b-2 border-verde-escuro">
        Script de Vendas
      </h3>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6 p-4 rounded-xl" style={{ backgroundColor: '#F8F4EF', border: '1px solid #EDE0D4' }}>
        <div className="text-sm" style={{ color: '#5F5E5A' }}>
          Perfil detectado:{' '}
          <span className="font-semibold" style={{ color: '#2D6A4F' }}>
            {perfilLabels[perfil]}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {(['1', '2', '3', '4'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setPerfilManual(p)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
              style={
                perfil === p
                  ? { backgroundColor: '#2D6A4F', color: '#FFFFFF' }
                  : { backgroundColor: '#FFFFFF', color: '#2D6A4F', border: '1px solid #2D6A4F' }
              }
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {[mensagem1, mensagem2, mensagem3].map((mensagem, idx) => (
          <div key={titulos[idx]} className="bg-bege-fundo p-6 rounded-xl">
            <h4 className="text-lg font-semibold text-verde-escuro mb-3">{titulos[idx]}</h4>
            <div className="text-texto-escuro whitespace-pre-wrap mb-4 text-sm leading-relaxed">
              {mensagem}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => copiarMensagem(mensagem, idx + 1)}
                className="flex-1 flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-2 rounded-xl hover:bg-opacity-90 transition-all text-sm font-medium"
              >
                <Copy className="w-4 h-4" />
                Copiar mensagem
              </button>
              <button
                onClick={() => abrirWhatsApp(mensagem)}
                className="flex-1 flex items-center justify-center gap-2 text-white px-4 py-2 rounded-xl transition-all text-sm font-medium"
                style={{ backgroundColor: '#25D366' }}
              >
                <MessageCircle className="w-4 h-4" />
                Abrir no WhatsApp
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ScriptVendas
